import { Controller } from "stimulus"

const HANDLE_ALLOWANCE = 24

const FALLBACK_BOUNDS = { leftMin: 210, rightMin: 240, centerMin: 680 }

export default class extends Controller {
  static targets = ["leftPane", "centerPane", "rightPane", "leftHandle", "rightHandle"]
  static values = {
    storageKey: String,
    leftDefault: Number,
    leftMax: Number,
    rightDefault: Number,
    rightMax: Number
  }

  connect() {
    this.leftWidth = this.leftDefault
    this.rightWidth = this.rightDefault

    this.pointerMoveHandler = this.onPointerMove.bind(this)
    this.pointerUpHandler = this.stopDragging.bind(this)
    this.keydownHandler = this.onKeydown.bind(this)
    this.resizeHandler = this.onResize.bind(this)

    this.readLayoutBounds()
    this.restoreWidths()
    this.applyWidths()

    this.bindHandle(this.leftHandleTarget)
    this.bindHandle(this.rightHandleTarget)
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    this.unbindHandle(this.leftHandleTarget)
    this.unbindHandle(this.rightHandleTarget)
    this.stopDragging()
    window.removeEventListener("resize", this.resizeHandler)
  }

  bindHandle(handle) {
    if (!handle) return

    handle.addEventListener("pointerdown", this.startDragging)
    handle.addEventListener("keydown", this.keydownHandler)
  }

  unbindHandle(handle) {
    if (!handle) return

    handle.removeEventListener("pointerdown", this.startDragging)
    handle.removeEventListener("keydown", this.keydownHandler)
  }

  startDragging = (event) => {
    event.preventDefault()

    this.dragSide = event.currentTarget.dataset.paneResizeSideParam
    this.element.classList.add("pane-resize--dragging")

    window.addEventListener("pointermove", this.pointerMoveHandler)
    window.addEventListener("pointerup", this.pointerUpHandler)
  }

  stopDragging() {
    this.dragSide = null
    this.element.classList.remove("pane-resize--dragging")

    window.removeEventListener("pointermove", this.pointerMoveHandler)
    window.removeEventListener("pointerup", this.pointerUpHandler)
  }

  onPointerMove(event) {
    if (!this.dragSide) return

    const rect = this.element.getBoundingClientRect()
    const effective = this.effectiveWidths()

    if (this.dragSide === "left") {
      const maxLeft = rect.width - effective.right - this.centerMin - HANDLE_ALLOWANCE
      this.leftWidth = this.clamp(event.clientX - rect.left, this.leftMin, Math.min(this.leftMax, maxLeft))
    } else {
      const maxRight = rect.width - effective.left - this.centerMin - HANDLE_ALLOWANCE
      this.rightWidth = this.clamp(rect.right - event.clientX, this.rightMin, Math.min(this.rightMax, maxRight))
    }

    this.applyWidths()
    this.persistWidths()
  }

  onKeydown(event) {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return

    event.preventDefault()

    const step = event.key === "ArrowLeft" ? -16 : 16
    const side = event.currentTarget.dataset.paneResizeSideParam

    if (side === "left") {
      this.leftWidth = this.clamp(this.leftWidth + step, this.leftMin, this.leftMax)
    } else {
      this.rightWidth = this.clamp(this.rightWidth - step, this.rightMin, this.rightMax)
    }

    this.applyWidths()
    this.persistWidths()
  }

  // The breakpoint owns the floors, not this controller: reading them back means a
  // tablet's narrower panes are respected instead of being overridden by a desktop
  // constant. Never write --pane-center-min here — an inline value outranks the
  // media query and pins the centre pane at its desktop minimum on every screen.
  readLayoutBounds() {
    const styles = getComputedStyle(this.element)
    const px = (name, fallback) => {
      const value = parseFloat(styles.getPropertyValue(name))
      return Number.isFinite(value) ? value : fallback
    }

    this.bounds = {
      leftMin: px("--pane-left-min", FALLBACK_BOUNDS.leftMin),
      rightMin: px("--pane-right-min", FALLBACK_BOUNDS.rightMin),
      centerMin: px("--pane-center-min", FALLBACK_BOUNDS.centerMin)
    }
  }

  applyWidths() {
    const { left, right } = this.effectiveWidths()

    this.element.style.setProperty("--pane-left-width", `${left}px`)
    this.element.style.setProperty("--pane-right-width", `${right}px`)
  }

  // Derives what actually fits right now, leaving the stored preference untouched so
  // that narrowing the window (or rotating a tablet) doesn't destroy it.
  effectiveWidths() {
    const rect = this.element.getBoundingClientRect()
    const availableWidth = rect.width || window.innerWidth
    const leftMin = this.leftMin
    const rightMin = this.rightMin

    let left = this.clamp(this.leftWidth, leftMin, this.leftMax)
    let right = this.clamp(this.rightWidth, rightMin, this.rightMax)

    const maxSideSpace = Math.max(leftMin + rightMin, availableWidth - this.centerMin - HANDLE_ALLOWANCE)
    let overflow = left + right - maxSideSpace
    if (overflow <= 0) return { left, right }

    const leftShrink = left - leftMin
    const rightShrink = right - rightMin
    const totalShrink = leftShrink + rightShrink
    if (totalShrink <= 0) return { left, right }

    const leftReduction = Math.min(leftShrink, Math.round((leftShrink / totalShrink) * overflow))
    left -= leftReduction
    overflow -= leftReduction

    right -= Math.min(rightShrink, overflow)

    return { left, right }
  }

  restoreWidths() {
    if (!this.hasStorageKeyValue) return

    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKeyValue))
      if (!saved) return

      this.leftWidth = this.clamp(saved.left, this.leftMin, this.leftMax)
      this.rightWidth = this.clamp(saved.right, this.rightMin, this.rightMax)
    } catch (_error) {
      this.leftWidth = this.leftDefault
      this.rightWidth = this.rightDefault
    }
  }

  persistWidths() {
    if (!this.hasStorageKeyValue) return

    localStorage.setItem(this.storageKeyValue, JSON.stringify({
      left: this.leftWidth,
      right: this.rightWidth
    }))
  }

  clamp(value, min, max) {
    return Math.min(Math.max(max, min), Math.max(min, value))
  }

  onResize() {
    this.readLayoutBounds()
    this.applyWidths()
  }

  get leftDefault() {
    return this.leftDefaultValue || 250
  }

  get leftMin() {
    return this.bounds.leftMin
  }

  get leftMax() {
    return this.leftMaxValue || 360
  }

  get rightDefault() {
    return this.rightDefaultValue || 300
  }

  get rightMin() {
    return this.bounds.rightMin
  }

  get rightMax() {
    return this.rightMaxValue || 360
  }

  get centerMin() {
    return this.bounds.centerMin
  }
}
